var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { noop, memoize } from '@ally/utilitarian';
import { modules, getModuleMetadata } from '../utils';
var loadModuleCacheKeyGen = function (_a) {
    var _b = __read(_a, 1), id = _b[0].id;
    return id;
};
var loadListenerOptions = {
    once: true,
    capture: true,
};
/**
 * Represents a collection of "federator" modules.
 * This class provides methods for fetching and evaluating modules that invoke
 * the "federator.register" method.
 */
var Modules = /** @class */ (function () {
    /**
     * Creates a new Modules instance.
     * Modules fetches will be cached by the module's id across ALL Module
     * instances.
     */
    function Modules(config) {
        var _this = this;
        /**
         * Loads a module from it's `source` by:
         *   - Resolving the module's URL.
         *   - Creating a script element pointed at the module's source.
         *   - Injecting the script into the head (and evaluating its code).
         *
         * If the module fails to load, it will be cleaned up from the global
         * modules store so subsequent calls will attempt to reload it.
         */
        this.loadAndEvalModule = function (app) {
            return new Promise(function (resolve, reject) {
                var id = app.id, source = app.source;
                var _a = _this.config, log = _a.log, host = _a.host, _b = _a.onModuleLoaded, onModuleLoaded = _b === void 0 ? noop : _b;
                var url = _this.resolve(source);
                var start = Date.now();
                var script = document.createElement('script');
                log.debug({
                    message: "Loading module at: " + url,
                });
                script.setAttribute('src', source);
                script.setAttribute('async', '');
                script.setAttribute('data-remote-id', id);
                // Invoked once the script has been fetched and loaded into the DOM.
                // Which means it's code has already been evaluated.
                var handleScriptLoad = function () {
                    var _a;
                    (_a = script.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(script);
                    var module = modules.get(id);
                    if (!(module === null || module === void 0 ? void 0 : module.loaded)) {
                        modules.delete(id);
                        return reject(new Error('Module failed to call `register`.'));
                    }
                    log.debug({
                        message: "Loaded module " + url + " in " + (Date.now() - start) + "ms",
                    });
                    onModuleLoaded(module);
                    return resolve(module);
                };
                var handleScriptError = function (_) {
                    var _a;
                    modules.delete(id);
                    (_a = script.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(script);
                    reject(new Error("Module at " + url + " failed to load."));
                };
                script.addEventListener('load', handleScriptLoad, loadListenerOptions);
                script.addEventListener('error', handleScriptError, loadListenerOptions);
                modules.set(id, {
                    meta: getModuleMetadata(host, url),
                    source: url,
                    loaded: false,
                    exports: {},
                    externals: _this.config.externals,
                });
                document.head.appendChild(script);
            });
        };
        /**
         * A memoized version of `loadAndEvalModule`.
         * Used to build the public `get` method below. Function calls are memoized
         * based on the value of the app's name. Also note, this caches the returned
         * Promise from the call to `loadAndEvalModule`, not the resolved value.
         */
        this.loadModuleMemo = memoize(this.loadAndEvalModule, loadModuleCacheKeyGen);
        this.config = config;
    }
    /**
     * Resolves a module's source URL.
     * Given some URL (relative or absolute), this will resolve it based on the
     * `host` value provided to Modules#constructor.config.host.
     */
    Modules.prototype.resolve = function (source) {
        var url = new URL(source, this.config.host);
        return url.protocol + "//" + url.host + url.pathname;
    };
    Object.defineProperty(Modules.prototype, "cache", {
        /**
         * Exposes the module cache.
         * Avoid messing with this.
         */
        get: function () {
            return this.loadModuleMemo.cache;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Returns true if the module already exists, false otherwise.
     */
    Modules.prototype.has = function (url) {
        return this.loadModuleMemo.cache.has(url);
    };
    /**
     * Fetches and loads the exports of a remote module.
     * Uses the memoized `fetchModuleMemo` as a base, but deletes and cache values
     * if the cached Promise is rejected. This allows the user to "retry" loading
     * the module if something goes wrong.
     */
    Modules.prototype.get = function (app) {
        var _this = this;
        return this.loadModuleMemo(app).catch(function (e) {
            _this.loadModuleMemo.cache.delete(loadModuleCacheKeyGen([app]));
            throw e;
        });
    };
    return Modules;
}());
export default Modules;
//# sourceMappingURL=Modules.js.map